import Alpine from 'alpinejs'
import Chart from 'chart.js/auto'
import { BoxPlotChart } from '@sgratzl/chartjs-chart-boxplot'

document.addEventListener('alpine:init', () => {
  Alpine.data('heatDemandScenarioStock', () => {
    const dwellingTypes = ['Detached', 'Semi-Detached', 'Terraced', 'Flat']
    let dwellingTypesChart
    let dwellingTypesInput // To store chart
    let heatingSystemSizeChart
    let heatingSystemSizeInput
    let heatDemandChart
    let heatDemandInput

    // Destroy the charts before swapping to ensure we don't get phantom chart resizes
    document.addEventListener('htmx:beforeSwap', function (event) {
      if (dwellingTypesChart) {
        dwellingTypesChart.destroy()
        dwellingTypesChart = null
      }
      if (heatingSystemSizeChart) {
        heatingSystemSizeChart.destroy()
        heatingSystemSizeChart = null
      }
      if (heatDemandChart) {
        heatDemandChart.destroy()
        heatDemandChart = null
      }
    })

    return {
      data: null,

      init () {
        fetch('./building-stock.json').then(response => response.json()).then(data => {
          this.data = data
          setTimeout(() => this.doBindings(), 100)
        })
      },

      doBindings () {
        const store = Alpine.store('scenarioState')

        // Initialise the charts
        dwellingTypesChart = new Chart(document.getElementById('dwellingTypesChart'), {
          type: 'bar',
          data: {
            labels: dwellingTypes,
            datasets: [{}, {}, {}]
          },
          options: {
            maintainAspectRatio: false,
            scales: {
              y: {
                display: false
              },
              dwellingCount: {
                position: 'left',
                title: {
                  display: true,
                  text: 'Number of dwellings'
                }
              },
              heatPumpCount: {
                position: 'right',
                title: {
                  display: true,
                  text: 'Number of heat pumps'
                }
              }
            },
            plugins: {
              legend: {
                display: true
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    // Show dwelling counts in millions with decimals, heat pump counts as integers with thousands separator
                    if (context.dataset.yAxisID === 'dwellingCount') {
                      return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}`
                    } else {
                      return `${context.dataset.label}: ${Math.round(context.parsed.y).toLocaleString()}`
                    }
                  }
                }
              }
            }
          }
        })
        dwellingTypesInput = document.getElementById('dwellingTypesChart-data')

        heatingSystemSizeChart = new BoxPlotChart(document.getElementById('heatingSystemSizeChart'), {
          data: {
            labels: dwellingTypes,
            datasets: [
              {
                label: 'Heating system size',
                data: []
              }
            ]
          },
          options: {
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Average heating system size (kW)',
                  font: { size: 10 }
                }
              }
            },
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return [
                      context.dataset.label,
                        `Median: ${Math.round(context.parsed.y)} kW`,
                        `Min: ${Math.round(context.parsed.min)} kW`,
                        `Max: ${Math.round(context.parsed.max)} kW`,
                        `Q1: ${Math.round(context.parsed.q1)} kW`,
                        `Q3: ${Math.round(context.parsed.q3)} kW`
                    ]
                  }
                }
              }
            }
          }
        })
        heatingSystemSizeInput = document.getElementById('heatingSystemSizeChart-data')

        heatDemandChart = new BoxPlotChart(document.getElementById('heatDemandChart'), {
          data: {
            labels: dwellingTypes,
            datasets: [
              {
                label: 'Heat demand',
                data: []
              }
            ]
          },
          options: {
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Average annual heat demand (MWh)',
                  font: { size: 10 }
                }
              }
            },
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return [
                      context.dataset.label,
                        `Median: ${Math.round(context.parsed.y)} MWh`,
                        `Min: ${Math.round(context.parsed.min)} MWh`,
                        `Max: ${Math.round(context.parsed.max)} MWh`,
                        `Q1: ${Math.round(context.parsed.q1)} MWh`,
                        `Q3: ${Math.round(context.parsed.q3)} MWh`
                    ]
                  }
                }
              }
            }
          }
        })
        heatDemandInput = document.getElementById('heatDemandChart-data')

        // As selection changes, update the charts
        // We register this in the import callback to ensure chart objects are available
        Alpine.effect(() => {
          const year = store.selectedYear
          if (!this.data) return

          const geoFilter = (entry) => {
            switch (store.selectedSpatialResolution) {
              case 'GB':
                return entry.Country_name === 'All' && entry.Local_authority_name === 'All'
              case 'Country':
                return entry.Country_name === store.selectedCountry && entry.Local_authority_name === 'All'
              case 'LA':
                return entry.Local_authority_name === store.selectedLA
            }
          }

          // Update the charts
          if (dwellingTypesChart) {
            const counts = {}
            const ahspCounts = {}
            const ghspCounts = {}
            const divisor = this.dwellingCountMagnitudeDivisor
            this.data.dwellingCounts[year]?.filter(geoFilter).forEach(entry => {
              counts[entry.Built_form] = parseFloat(entry['Number_of_dwellings']) / divisor
              ahspCounts[entry.Built_form] = parseFloat(entry.stock_ASHP)
              ghspCounts[entry.Built_form] = parseFloat(entry.stock_GSHP)
            })
            dwellingTypesChart.data.datasets[0].label = `Dwellings (${this.dwellingCountMagnitudeText})`
            dwellingTypesChart.data.datasets[0].data = [counts.detached, counts['semi-detached'], counts.terraced, counts.flat]
            dwellingTypesChart.data.datasets[0].yAxisID = 'dwellingCount'
            dwellingTypesChart.data.datasets[1].label = 'Air source heat pumps'
            dwellingTypesChart.data.datasets[1].data = [ahspCounts.detached, ahspCounts['semi-detached'], ahspCounts.terraced, ahspCounts.flat]
            dwellingTypesChart.data.datasets[1].yAxisID = 'heatPumpCount'
            dwellingTypesChart.data.datasets[2].label = 'Ground source heat pumps'
            dwellingTypesChart.data.datasets[2].data = [ghspCounts.detached, ghspCounts['semi-detached'], ghspCounts.terraced, ghspCounts.flat]
            dwellingTypesChart.data.datasets[2].yAxisID = 'heatPumpCount'
            dwellingTypesChart.options.scales.dwellingCount.title.text = `Number of dwellings (${this.dwellingCountMagnitudeText})`
            dwellingTypesChart.update()
            dwellingTypesInput.value = JSON.stringify(dwellingTypesChart.data)
          }

          if (heatDemandChart) {
            const dataSet = {}
            this.data.heatDemands[year]?.filter(geoFilter).forEach(entry => {
              dataSet[entry.Built_form] = {
                min: parseFloat(entry['Lower Fence']) / 1000,
                q1: parseFloat(entry.Q1) / 1000,
                median: parseFloat(entry.Median) / 1000,
                q3: parseFloat(entry.Q3) / 1000,
                max: parseFloat(entry['Upper Fence']) / 1000
              }
            })
            heatDemandChart.data.datasets[0].data = [dataSet.detached, dataSet['semi-detached'], dataSet.terraced, dataSet.flat]
            heatDemandChart.update()
            heatDemandInput.value = JSON.stringify(heatDemandChart.data)
          }

          if (heatingSystemSizeChart) {
            const dataSet = {}
            this.data.heatSizes[year]?.filter(geoFilter).forEach(entry => {
              dataSet[entry.Built_form] = {
                min: parseFloat(entry['Lower Fence']),
                q1: parseFloat(entry.Q1),
                median: parseFloat(entry.Median),
                q3: parseFloat(entry.Q3),
                max: parseFloat(entry['Upper Fence'])
              }
            })
            heatingSystemSizeChart.data.datasets[0].data = [dataSet.detached, dataSet['semi-detached'], dataSet.terraced, dataSet.flat]
            heatingSystemSizeChart.update()
            heatingSystemSizeInput.value = JSON.stringify(heatingSystemSizeChart.data)
          }
        })
      },
      get dwellingCountMagnitudeText () {
        const res = Alpine.store('scenarioState').selectedSpatialResolution
        if (res === 'GB') return 'million'
        if (res === 'Country') return 'million'
        if (res === 'LA') return 'thousand'
      },
      get dwellingCountMagnitudeDivisor () {
        const res = Alpine.store('scenarioState').selectedSpatialResolution
        if (res === 'GB') return 1000000
        if (res === 'Country') return 1000000
        if (res === 'LA') return 1000
      },
      get numberOfDwellings () {
        let count = 0
        const store = Alpine.store('scenarioState')
        const geoFilter = (entry) => {
          if (store.selectedSpatialResolution === 'GB') return entry.Country_name === 'All' && entry.Local_authority_name === 'All'
          if (store.selectedSpatialResolution === 'Country') return entry.Country_name === store.selectedCountry && entry.Local_authority_name === 'All'
          if (store.selectedSpatialResolution === 'LA') return entry.Local_authority_name === store.selectedLA
        }
        const divisor = this.dwellingCountMagnitudeDivisor
        this.data?.dwellingCounts[store.selectedYear]?.filter(geoFilter)?.forEach(entry => {
          count += parseFloat(entry['Number_of_dwellings'] / divisor)
        })
        return count.toFixed(1)
      }
    }
  })
})
